import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  Collapse,
  CircularProgress,
  FormControl,
  InputLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Popover,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  TablePagination,
} from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon, Add as AddIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { testsApi } from '@/api/tests'
import { getErrorMessage } from '@/utils/error'
import { pickParticipantForListScore } from '@/utils/pickParticipantForListScore'
import { formatTestListScore } from '@/utils/testListScoreDisplay'
import type { Test, TestStatus } from '@/types'

const statusOrder: TestStatus[] = ['active', 'draft', 'completed', 'expired', 'cancelled']

type KindFilter = 'all' | 'self' | 'teacher'

function runStatusChipProps(status: TestStatus): { color: 'default' | 'primary' | 'success' | 'error' | 'warning'; variant?: 'filled' | 'outlined' } {
  if (status === 'active') return { color: 'primary', variant: 'filled' }
  if (status === 'completed') return { color: 'success', variant: 'filled' }
  if (status === 'cancelled') return { color: 'error', variant: 'filled' }
  if (status === 'expired') return { color: 'warning', variant: 'filled' }
  if (status === 'draft') return { color: 'warning', variant: 'outlined' }
  return { color: 'default', variant: 'filled' }
}

function fmtRunDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return '—'
  const d = dayjs(iso).locale(lang === 'ru' ? 'ru' : 'en')
  if (!d.isValid()) return '—'
  return d.format('DD.MM.YYYY HH:mm')
}

function fmtDurationSec(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '—'
  const s = Math.floor(sec)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  if (h > 0) return `${h}:${pad(m)}:${pad(ss)}`
  return `${m}:${pad(ss)}`
}

export function TestsPage() {
  const { t, i18n } = useTranslation()
  const me = useAuthStore((s) => s.user)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<Test[]>([])
  const [meta, setMeta] = useState<{ current_page: number; last_page: number; per_page: number; total: number }>({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  })

  const meId = me?.id ?? null
  const canLoad = useMemo(() => typeof meId === 'number' && Number.isFinite(meId), [meId])

  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<TestStatus[]>(['active'])
  const [kindFilter, setKindFilter] = useState<KindFilter>('all')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState<HTMLElement | null>(null)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(15)

  type SortPreset =
    | 'id_desc'
    | 'id_asc'
    | 'status_asc'
    | 'status_desc'
    | 'started_desc'
    | 'started_asc'
    | 'ended_desc'
    | 'ended_asc'
    | 'score_desc'
    | 'score_asc'
  const [sortPreset, setSortPreset] = useState<SortPreset>('started_desc')

  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (statusFilter.length > 0) count += 1
    if (fromDate || toDate) count += 1
    if (kindFilter !== 'all') count += 1
    return count
  }, [fromDate, kindFilter, statusFilter, toDate])

  const openDateRange = (e: MouseEvent<HTMLElement>) => {
    setDateRangeAnchorEl(e.currentTarget)
  }
  const closeDateRange = () => setDateRangeAnchorEl(null)
  const dateCalendarValue: Dayjs | null = toDate ? dayjs(toDate) : fromDate ? dayjs(fromDate) : null

  const dateRangeLabel = useMemo(() => {
    if (fromDate && toDate) return `${dayjs(fromDate).format('DD.MM.YYYY')} - ${dayjs(toDate).format('DD.MM.YYYY')}`
    if (fromDate) return t('tests.filters.dateFrom', { defaultValue: `с ${dayjs(fromDate).format('DD.MM.YYYY')}` })
    if (toDate) return t('tests.filters.dateTo', { defaultValue: `до ${dayjs(toDate).format('DD.MM.YYYY')}` })
    return ''
  }, [fromDate, toDate, t])

  const handleDateCalendarChange = (value: Dayjs | null) => {
    if (!value) return
    const picked = value.format('YYYY-MM-DD')
    if (!fromDate || toDate) {
      setFromDate(picked)
      setToDate('')
      return
    }
    if (picked < fromDate) {
      setFromDate(picked)
      setToDate(fromDate)
    } else {
      setToDate(picked)
    }
    closeDateRange()
  }

  const handleStatusChange = (e: SelectChangeEvent<typeof statusFilter>) => {
    const value = e.target.value
    if (typeof value === 'string') {
      setStatusFilter(value ? ([value] as TestStatus[]) : [])
      return
    }
    setStatusFilter(value as TestStatus[])
  }

  useEffect(() => {
    setPage(0)
  }, [fromDate, toDate, statusFilter, sortPreset, kindFilter])

  const sortParams = useMemo((): { sort?: 'id' | 'status' | 'started_at' | 'ended_at' | 'results_score'; order?: 'asc' | 'desc' } => {
    switch (sortPreset) {
      case 'id_asc':
        return { sort: 'id', order: 'asc' }
      case 'id_desc':
        return { sort: 'id', order: 'desc' }
      case 'status_asc':
        return { sort: 'status', order: 'asc' }
      case 'status_desc':
        return { sort: 'status', order: 'desc' }
      case 'started_asc':
        return { sort: 'started_at', order: 'asc' }
      case 'started_desc':
        return { sort: 'started_at', order: 'desc' }
      case 'ended_asc':
        return { sort: 'ended_at', order: 'asc' }
      case 'ended_desc':
        return { sort: 'ended_at', order: 'desc' }
      case 'score_asc':
        return { sort: 'results_score', order: 'asc' }
      case 'score_desc':
        return { sort: 'results_score', order: 'desc' }
      default:
        return { sort: 'started_at', order: 'desc' }
    }
  }, [sortPreset])

  const fetchRuns = useCallback(async () => {
    if (!canLoad) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await testsApi.list({
        user_id: meId!,
        status: statusFilter.length ? statusFilter : undefined,
        source: kindFilter === 'all' ? undefined : kindFilter,
        from: fromDate || undefined,
        to: toDate || undefined,
        page: page + 1,
        per_page: perPage,
        ...sortParams,
      })
      setRows(res.data ?? [])
      setMeta({
        current_page: res.current_page ?? 1,
        last_page: res.last_page ?? 1,
        per_page: res.per_page ?? perPage,
        total: res.total ?? 0,
      })
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setIsLoading(false)
    }
  }, [canLoad, fromDate, kindFilter, meId, page, perPage, sortParams, statusFilter, toDate])

  useEffect(() => {
    void fetchRuns()
  }, [fetchRuns])

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <RouterLink
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </RouterLink>
          <Typography color="text.primary">{t('tests.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('tests.title')}
          </Typography>
          <PageHelpPopover pageKey="tests" />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t('tests.sort.label', { defaultValue: 'Сортировка' })}</InputLabel>
            <Select
              value={sortPreset}
              label={t('tests.sort.label', { defaultValue: 'Сортировка' })}
              onChange={(e) => setSortPreset(e.target.value as SortPreset)}
            >
              <MenuItem value="started_desc">{t('tests.sort.started_desc', { defaultValue: 'Начало: новые' })}</MenuItem>
              <MenuItem value="started_asc">{t('tests.sort.started_asc', { defaultValue: 'Начало: старые' })}</MenuItem>
              <MenuItem value="ended_desc">{t('tests.sort.ended_desc', { defaultValue: 'Окончание: новые' })}</MenuItem>
              <MenuItem value="ended_asc">{t('tests.sort.ended_asc', { defaultValue: 'Окончание: старые' })}</MenuItem>
              <MenuItem value="id_desc">{t('tests.sort.id_desc', { defaultValue: 'ID: по убыванию' })}</MenuItem>
              <MenuItem value="id_asc">{t('tests.sort.id_asc', { defaultValue: 'ID: по возрастанию' })}</MenuItem>
              <MenuItem value="status_asc">{t('tests.sort.status_asc', { defaultValue: 'Статус: A→Z' })}</MenuItem>
              <MenuItem value="status_desc">{t('tests.sort.status_desc', { defaultValue: 'Статус: Z→A' })}</MenuItem>
              <MenuItem value="score_desc">{t('tests.sort.score_desc', { defaultValue: 'Балл: высокий' })}</MenuItem>
              <MenuItem value="score_asc">{t('tests.sort.score_asc', { defaultValue: 'Балл: низкий' })}</MenuItem>
            </Select>
          </FormControl>

          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('tests.filters.button', { defaultValue: 'Фильтры' })}
          />

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Tooltip title={t('tests.actions.createSelf')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  component={RouterLink}
                  to={ROUTES.SELF_TEST_CREATE}
                  aria-label={t('tests.actions.createSelf')}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={filtersOpen} unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Box sx={{ minWidth: 260, flex: 1 }}>
                <TextField
                  label={t('tests.filters.dateRange', { defaultValue: 'Период' })}
                  value={dateRangeLabel}
                  placeholder={t('tests.filters.dateRangePlaceholder', { defaultValue: 'Выберите даты' })}
                  onClick={openDateRange}
                  fullWidth
                  size="small"
                  inputProps={{ readOnly: true }}
                />
                <Popover
                  open={Boolean(dateRangeAnchorEl)}
                  anchorEl={dateRangeAnchorEl}
                  onClose={closeDateRange}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2, width: 320 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                      <DateCalendar value={dateCalendarValue} onChange={handleDateCalendarChange} />
                    </LocalizationProvider>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setFromDate('')
                          setToDate('')
                          closeDateRange()
                        }}
                      >
                        {t('common.reset', { defaultValue: 'Сбросить' })}
                      </Button>
                      <Button size="small" variant="contained" color="secondary" onClick={closeDateRange}>
                        {t('common.ok', { defaultValue: 'Ок' })}
                      </Button>
                    </Box>
                  </Box>
                </Popover>
              </Box>

              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>{t('tests.filters.status', { defaultValue: 'Статус' })}</InputLabel>
                <Select
                  multiple
                  value={statusFilter}
                  label={t('tests.filters.status', { defaultValue: 'Статус' })}
                  onChange={handleStatusChange}
                  renderValue={(selected) => {
                    const arr = selected as TestStatus[]
                    if (arr.length === 0) return t('tests.filters.allStatuses', { defaultValue: 'Все статусы' })
                    return arr.map((s) => t(`tests.runStatus.${s}`, { defaultValue: s })).join(', ')
                  }}
                >
                  {statusOrder.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Checkbox checked={statusFilter.indexOf(s) > -1} />
                      {t(`tests.runStatus.${s}`, { defaultValue: s })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('tests.filters.kind', { defaultValue: 'Тип' })}</InputLabel>
                <Select
                  value={kindFilter}
                  label={t('tests.filters.kind', { defaultValue: 'Тип' })}
                  onChange={(e) => setKindFilter(e.target.value as KindFilter)}
                >
                  <MenuItem value="all">{t('tests.filters.kindAll', { defaultValue: 'Все типы' })}</MenuItem>
                  <MenuItem value="teacher">{t('tests.kind.teacher')}</MenuItem>
                  <MenuItem value="self">{t('tests.kind.self')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Collapse>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('tests.table.id')}</TableCell>
                  <TableCell>{t('tests.table.startedAt', { defaultValue: 'Начало' })}</TableCell>
                  <TableCell>{t('tests.table.endedAt', { defaultValue: 'Окончание' })}</TableCell>
                  <TableCell>{t('tests.table.duration', { defaultValue: 'Длительность' })}</TableCell>
                  <TableCell>{t('tests.table.status')}</TableCell>
                  <TableCell>{t('tests.table.score', { defaultValue: 'Балл' })}</TableCell>
                  <TableCell>{t('tests.table.kind', { defaultValue: 'Тип' })}</TableCell>
                  <TableCell>{t('tests.table.lesson')}</TableCell>
                  <TableCell align="right">{t('tests.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary">
                        {t('tests.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {rows.map((r) => {
                  const st = runStatusChipProps(r.status)
                  const p = typeof meId === 'number' && Number.isFinite(meId) ? pickParticipantForListScore(r, meId) : null
                  const score = p?.results_score
                  const duration =
                    r.duration_sec != null && r.duration_sec > 0
                      ? fmtDurationSec(r.duration_sec)
                      : r.started_at && r.ended_at
                        ? fmtDurationSec(Math.max(0, Math.round((dayjs(r.ended_at).valueOf() - dayjs(r.started_at).valueOf()) / 1000)))
                        : '—'
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{fmtRunDate(r.started_at, i18n.language)}</TableCell>
                      <TableCell>{fmtRunDate(r.ended_at, i18n.language)}</TableCell>
                      <TableCell>{duration}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(`tests.runStatus.${r.status}`, { defaultValue: r.status })}
                          color={st.color}
                          variant={st.variant}
                        />
                      </TableCell>
                      <TableCell>{formatTestListScore(score)}</TableCell>
                      <TableCell>
                        {r.lesson_id != null ? t('tests.kind.teacher', { defaultValue: 'С учителем' }) : t('tests.kind.self', { defaultValue: 'Самостоятельный' })}
                      </TableCell>
                      <TableCell>
                        {r.lesson_id != null ? (
                          <Link component={RouterLink} to={ROUTES.LESSON_DETAILS(r.lesson_id)} underline="hover">
                            № {r.lesson_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('tests.actions.open')}>
                          <IconButton
                            size="small"
                            component={RouterLink}
                            to={ROUTES.TEST_RUN(r.id)}
                            aria-label={t('tests.actions.open')}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && meta.total > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <TablePagination
              component="div"
              count={meta.total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => {
                setPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 15, 25, 50, 100]}
              labelRowsPerPage={t('words.rows_per_page', { defaultValue: 'На странице' })}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('words.of', { defaultValue: 'из' })} ${count !== -1 ? count : to}`
              }
              sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}
            />
          </Box>
        )}
      </Box>
    </DashboardLayout>
  )
}

